import React from "react";
import { formatToDateFull } from "../master/FormatToDateTime";
import {
  buildMarksheet,
  getRemark,
  nextClass,
  getSummaryItems,
  FILL_IN_ITEMS,
} from "./marksheetUtils";
import {
  useSetting,
  getLogo,
  schoolAddress,
  schoolContact,
  getStudentFields,
  MarksheetStyle,
  MarksTable,
  CoScholastic,
  GradeLegend,
} from "./marksheetShared";

// Marksheet 3: Formal / traditional. Serif type, double-line frame,
// dotted leader lines for details, ruled tables.

const CSS = `
& { padding: 8mm 9mm; border: 4px double #000 !important; font-family: Georgia, "Times New Roman", serif; }
& .ms3-head { display: grid; grid-template-columns: 72px 1fr 72px; align-items: center; padding-bottom: 8px; border-bottom: 3px double #000 !important; }
& .ms3-logo { width: 66px; height: 66px; object-fit: contain; }
& .ms3-school { text-align: center; }
& .ms3-name { font-size: 22px; font-weight: 700; letter-spacing: .02em; }
& .ms3-meta { font-size: 10px; }
& .ms3-title { margin-top: 10px; text-align: center; font-size: 15px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase !important; }
& .ms3-session { margin-bottom: 8px; text-align: center; font-style: italic; }

& .ms3-details { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 24px; margin: 8px 0 10px; }
& .ms3-row { display: flex; gap: 6px; padding-bottom: 1px; border-bottom: 1px dotted #000 !important; }
& .ms3-row span { flex: 0 0 84px; }
& .ms3-row b { font-weight: 700; }

& .ms-marks th, & .ms-marks td, & .ms-cos th, & .ms-cos td,
& .ms3-result th, & .ms3-result td { border: .75pt solid #000 !important; text-align: center; padding: 4px 5px; }
& .ms-marks thead th, & .ms-cos th, & .ms3-result th { font-weight: 700; background: rgba(0,0,0,.09); }
& .ms-marks tfoot td { font-weight: 700; background: rgba(0,0,0,.05); }
& .ms-cos th:first-child, & .ms-cos td:first-child { text-align: left; }
& .ms-cos td { padding: 6px; }

& .ms3-result { margin-top: 8px; }
& .ms3-result td { height: 24px; font-weight: 700; }

& .ms3-lower { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 8px; align-items: start; }
& .ms3-remarks { padding: 6px 8px; border: .75pt solid #000 !important; }
& .ms3-line { display: flex; gap: 6px; min-height: 22px; margin-bottom: 8px; border-bottom: 1px dotted #000 !important; }
& .ms3-line span { flex: none; }
& .ms3-line b { font-weight: 700; }

& .ms3-sign { display: flex; justify-content: space-between; margin-top: 30px; }
& .ms3-sign div { width: 52mm; padding-top: 3px; text-align: center; border-top: 1px solid #000 !important; }
& .ms-legend { margin-top: 10px; font-style: italic; }
`;

export default function StudentMarksheet3({ data, batch, examDate }) {
  const setting = useSetting();
  const sheet = buildMarksheet(data, batch);
  const logo = getLogo(setting);
  const fields = getStudentFields(data, batch);
  const items = [
    ...getSummaryItems(sheet.overall),
    ...FILL_IN_ITEMS.map((label) => ({ label, value: "" })),
  ];
  const remark = getRemark(sheet.overall.percentage, data?.student_id);

  return (
    <>
      <MarksheetStyle setting={setting} scope="ms3" css={CSS} />

      <div className="a4-page-marksheet ms3">
        <div className="ms3-head">
          <div>{logo && <img className="ms3-logo" src={logo} alt="" />}</div>
          <div className="ms3-school">
            <div className="ms3-name">{setting?.name}</div>
            <div className="ms3-meta">{schoolAddress(setting)}</div>
            <div className="ms3-meta">{schoolContact(setting)}</div>
          </div>
          <div></div>
        </div>

        <div className="ms3-title">Statement of Marks</div>
        <div className="ms3-session">
          Academic Session {batch?.sessions?.session}
        </div>

        <div className="ms3-details">
          {fields.map((f) => (
            <div className="ms3-row" key={f.label}>
              <span>{f.label}</span>
              <b>{f.value || "-"}</b>
            </div>
          ))}
        </div>

        <MarksTable {...sheet} />

        <table className="ms3-result">
          <thead>
            <tr>
              {items.map((t) => (
                <th key={t.label}>{t.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {items.map((t) => (
                <td key={t.label}>{t.value}</td>
              ))}
            </tr>
          </tbody>
        </table>

        <div className="ms3-lower">
          <CoScholastic />
          <div className="ms3-remarks">
            <div className="ms3-line">
              <span>Remark:</span>
              <b>{remark}</b>
            </div>
            <div className="ms3-line">
              <span>Promoted to class:</span>
              <b>{nextClass(batch?.course?.title)}</b>
            </div>
            <div className="ms3-line">
              <span>Date:</span>
              <b>{formatToDateFull(examDate)}</b>
            </div>
          </div>
        </div>

        <div className="ms3-sign">
          <div>Class Teacher</div>
          <div>Parent / Guardian</div>
          <div>Principal</div>
        </div>

        <GradeLegend />
      </div>
    </>
  );
}
